import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AdminLayout } from "@/components/Layouts";
import { DashboardCard } from "@/components/DashboardCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TradingChartLoader } from "@/components/TradingChartLoader";
import { Users, UserCheck, Clock, XCircle, Layers, Video, BookOpen, FileText, CalendarRange, TrendingUp, ArrowRight, UserPlus, Check, Globe, ExternalLink, Save } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [savingUrl, setSavingUrl] = useState(false);

  const load = async () => {
    const [s, settings] = await Promise.all([
      api.get("/admin/dashboard").then((r) => r.data).catch(() => null),
      api.get("/settings").then((r) => r.data).catch(() => ({})),
    ]);
    setStats(s);
    setWebsiteUrl(settings?.website_url || "https://elearningsaham.my");
  };

  useEffect(() => { load(); }, []);

  const saveWebsiteUrl = async () => {
    setSavingUrl(true);
    try {
      await api.patch("/admin/settings", { website_url: websiteUrl });
      toast.success("Website link updated.");
    } catch {
      toast.error("Gagal kemas kini link.");
    } finally {
      setSavingUrl(false);
    }
  };

  const quickApprove = async (id) => {
    try {
      await api.post(`/admin/students/${id}/approve`);
      toast.success("Student approved");
      load();
    } catch { toast.error("Gagal meluluskan"); }
  };

  if (!stats) return <AdminLayout title="Dashboard"><div className="flex items-center justify-center py-16"><TradingChartLoader size="lg" text="Loading dashboard..." /></div></AdminLayout>;

  return (
    <AdminLayout title="Admin Dashboard">
      <div className="mb-6">
        <h1 className="text-3xl font-heading font-bold tracking-tight">Admin Dashboard</h1>
        <p className="mt-2 text-muted-foreground">Overview of the eLearningSaham platform.</p>
      </div>

      {/* Website Link Quick Update */}
      <Card className="glass-card mb-6 relative overflow-hidden" data-testid="website-link-card">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
        <CardContent className="p-5 sm:p-6 relative">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="icon-tile h-11 w-11 shrink-0 hidden sm:inline-flex">
              <Globe className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-1">
                <Globe className="h-4 w-4 text-primary sm:hidden" />
                <h3 className="font-heading font-semibold text-base">Link Website</h3>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                The main website link shown in the footer and emails.
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <Label htmlFor="website-url" className="sr-only">Website URL</Label>
                <Input
                  id="website-url"
                  type="url"
                  value={websiteUrl}
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                  placeholder="https://elearningsaham.my"
                  className="flex-1"
                  data-testid="website-url-input"
                />
                <Button onClick={saveWebsiteUrl} disabled={savingUrl} data-testid="save-website-url-btn">
                  <Save className="h-4 w-4" /> {savingUrl ? "Saving..." : "Save"}
                </Button>
                {websiteUrl && (
                  <Button asChild variant="outline" data-testid="visit-website-btn">
                    <a href={websiteUrl.startsWith("http") ? websiteUrl : `https://${websiteUrl}`} target="_blank" rel="noreferrer">
                      <ExternalLink className="h-4 w-4" /> Visit
                    </a>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pending registrations alert */}
      {stats.pending_students > 0 && (
        <Card className="glass-card border-gold/40 mb-6 overflow-hidden relative" data-testid="pending-alert">
          <div className="absolute inset-0 bg-gradient-to-br from-gold/15 via-transparent to-transparent" />
          <CardContent className="p-6 relative">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center shrink-0">
                  <UserPlus className="h-5 w-5 text-gold" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg">
                    {stats.pending_students} pendaftaran baru menunggu pengesahan
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Review and approve new students so they can sign in to the portal.
                  </p>
                </div>
              </div>
              <Button asChild variant="outline" data-testid="view-pending-btn">
                <Link to="/admin/students?filter=pending">View All <ArrowRight className="h-3.5 w-3.5" /></Link>
              </Button>
            </div>
            {stats.recent_pending && stats.recent_pending.length > 0 && (
              <div className="mt-5 space-y-2">
                {stats.recent_pending.map((u) => (
                  <div key={u.id} className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card/50 p-3" data-testid={`pending-${u.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{u.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{u.email} · {u.phone || "—"}</p>
                    </div>
                    <Button size="sm" onClick={() => quickApprove(u.id)} className="btn-glow" data-testid={`quick-approve-${u.id}`}>
                      <Check className="h-3.5 w-3.5" /> Approvekan
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <DashboardCard icon3d="users"        icon={Users}         label="Total Students"       value={stats.total_students}      accent="navy"   testId="admin-stat-students" />
        <DashboardCard icon3d="check-circle" icon={UserCheck}     label="Active"               value={stats.active_students}     accent="teal"   testId="admin-stat-active" />
        <DashboardCard icon3d="sparkles"     icon={Clock}         label="Pending"              value={stats.pending_students}    accent="gold"   testId="admin-stat-pending" />
        <DashboardCard icon3d="shield"       icon={XCircle}       label="Expired"              value={stats.expired_students}    accent="danger" testId="admin-stat-expired" />
        <DashboardCard icon3d="layers"       icon={Layers}        label="Total Batches"        value={stats.total_batches}       accent="navy"   testId="admin-stat-batches" />
        <DashboardCard icon3d="video"        icon={Video}         label="Recordings Available" value={stats.recordings_available} accent="teal"   testId="admin-stat-rec" />
        <DashboardCard icon3d="book"         icon={BookOpen}      label="Module Available"     value={stats.modules_available}   accent="teal"   testId="admin-stat-modules" />
        <DashboardCard icon3d="document"     icon={FileText}      label="Notes Available"      value={stats.notes_available}     accent="teal"   testId="admin-stat-notes" />
        <DashboardCard icon3d="calendar"     icon={CalendarRange} label="Upcoming Events"      value={stats.upcoming_events}     accent="gold"   testId="admin-stat-events" />
      </div>

      <Card className="glass-card mt-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-heading font-semibold flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" /> Average Course Completion
            </h3>
            <span className="text-2xl font-heading font-bold text-primary">{stats.average_completion}%</span>
          </div>
          <Progress value={stats.average_completion} className="h-2" />
        </CardContent>
      </Card>
    </AdminLayout>
  );
}
